import React, { useState, useEffect } from 'react';
import { ExternalLink, Target, TrendingUp, Shield, Zap, Award, Calendar } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';
import dataService from '../services/dataService';

const TradeLikeMe = () => {
  usePageTitle('Trade Like Me');
  
  const [sharedMetrics, setSharedMetrics] = useState(null);
  
  // Calculate years since 2019 dynamically
  const currentYear = new Date().getFullYear();
  const yearsSince2019 = currentYear - 2019;
  const yearsText = yearsSince2019 === 1 ? "1 year" : `${yearsSince2019}+ years`;

  useEffect(() => {
    const loadMetrics = async () => {
      try {
        await dataService.loadData();
        const metrics = dataService.getSharedMetrics();
        setSharedMetrics(metrics);
      } catch (error) {
        console.error('Error loading metrics:', error);
      }
    };

    loadMetrics();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-4">
            <div className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-medium mb-6">
              <Award className="w-4 h-4 mr-2" />
              2x Zerodha 60 Day Challenge Winner
            </div>
          </div>
        </div>
      </section>

      {/* Platform Philosophy */}
      <section className="py-8 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">The QuantCents Philosophy</h2>
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <p className="text-xl text-gray-700 leading-relaxed mb-6 italic">
                "I have poured 100% of everything that I have learnt about trading in capital markets 
                and software algorithms over the last {yearsText} into this platform."
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Every strategy, every algorithm, every risk management technique has been battle-tested 
                with real money. This isn't theoretical knowledge – it's practical wisdom earned through 
                {yearsText} of profitable trading.
              </p>
            </div>
          </div>
          
          {/* CTAs */}
          <div className="text-center mt-12">
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="https://www.quantcents.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-800 transition-all duration-200 shadow-lg hover:shadow-xl group"
              >
                Visit QuantCents Platform
                <ExternalLink className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              
              <a
                href="https://calendly.com/kunal-twoandhalfcents/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-xl border-2 border-gray-200 hover:border-gray-400 hover:text-gray-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <Calendar className="mr-2 w-5 h-5" />
                Schedule Strategy Call
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What You'll Get</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Access to proven strategies and systems that have generated consistent profits
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Options Strategies */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <Target className="w-6 h-6 text-gray-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Options Strategies</h3>
              <p className="text-gray-600 mb-4">
                Advanced options trading strategies focusing on theta decay and volatility arbitrage.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Bull Call Ratio Spreads</li>
                <li>• QuantCents platform</li>
                <li>• VIX-based positioning</li>
              </ul>
            </div>

            {/* Algorithmic Systems */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <Zap className="w-6 h-6 text-gray-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Algorithmic Systems</h3>
              <p className="text-gray-600 mb-4">
                Automated trading systems built with Python and backtested on tick-by-tick data.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• 50+ strategy backtests</li>
                <li>• Monte Carlo simulations</li>
                <li>• Real-time execution</li>
              </ul>
            </div>

            {/* Risk Management */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-6">
                <Shield className="w-6 h-6 text-gray-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Risk Management</h3>
              <p className="text-gray-600 mb-4">
                Sophisticated risk controls and position sizing based on volatility and risk appetite.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Dynamic position sizing</li>
                <li>• Correlation analysis</li>
                <li>• Drawdown protection</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Track Record */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Proven Track Record</h2>
            <p className="text-xl text-gray-600">
              Real results from real money trading
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-2">2x</div>
              <div className="text-gray-600">Zerodha Challenge Winner</div>
              <div className="text-sm text-gray-500 mt-1">60-day trading competitions</div>
            </div>
            
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-2">
                {sharedMetrics ? `${sharedMetrics.cagr.toFixed(1)}%` : '24%'}
              </div>
              <div className="text-gray-600">CAGR</div>
              <div className="text-sm text-gray-500 mt-1">
                {sharedMetrics ? `${sharedMetrics.totalYears} years compounded` : 'Compounded returns'}
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-2">
                {sharedMetrics ? dataService.formatCurrency(sharedMetrics.bestYearPnL) : '₹23L+'}
              </div>
              <div className="text-gray-600">Best Year Net P&L</div>
              <div className="text-sm text-gray-500 mt-1">
                {sharedMetrics ? `on ${dataService.formatCurrency(sharedMetrics.bestYearCapital)} capital` : 'on ₹10L capital'}
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-2">50+</div>
              <div className="text-gray-600">Strategies Backtested</div>
              <div className="text-sm text-gray-500 mt-1">On tick-by-tick data</div>
            </div>
          </div>
          
          {/* Verification Section */}
          <div className="mt-12 text-center">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-green-100 max-w-2xl mx-auto">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <ExternalLink className="w-6 h-6 text-gray-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Transparent Performance</h3>
              <p className="text-gray-600 mb-6">
                Check my verified Zerodha P&L for last 365 days
              </p>
              <a
                href="https://console.zerodha.com/verified/38b21b04"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors duration-200"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                View Verified P&L
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Level Up Your Trading?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of traders who have transformed their approach to the markets. 
            Let's discuss your trading goals and create a personalized strategy.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://www.quantcents.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-xl hover:bg-gray-50 transition-colors duration-200 shadow-lg group"
            >
              <TrendingUp className="mr-2 w-5 h-5" />
              Explore QuantCents
              <ExternalLink className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            
            <a
              href="https://calendly.com/kunal-twoandhalfcents/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-transparent text-white font-semibold rounded-xl border-2 border-white hover:bg-white hover:text-gray-900 transition-all duration-200"
            >
              <Calendar className="mr-2 w-5 h-5" />
              Book Strategy Session
            </a>
          </div>
          
          <div className="mt-8 text-white/80 text-sm">
            <p>✓ 30-minute consultation • ✓ Personalized strategy discussion • ✓ No commitment required</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TradeLikeMe;